import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';

const AddPlaceDialog = ({ open, onOpenChange, onPlaceAdded }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'other',
    address: '',
    phone: '',
    website: '',
    notes: '',
    would_go_back: null,
    experience_rating: null,
    experience_notes: '',
    recommended_services: '',
    last_visit_date: '',
    is_favorite: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Place name is required');
      return;
    }
    
    setLoading(true);
    try {
      await API.post('/places', {
        ...formData,
        name: formData.name.trim(),
        last_visit_date: formData.last_visit_date || null,
      });
      onPlaceAdded();
      setFormData({
        name: '',
        category: 'other',
        address: '',
        phone: '',
        website: '',
        notes: '',
        would_go_back: null,
        experience_rating: null,
        experience_notes: '',
        recommended_services: '',
        last_visit_date: '',
        is_favorite: false,
      });
    } catch (error) {
      console.error('Failed to add place:', error);
      toast.error('Failed to add place');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Place</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="place_name">Place Name *</Label>
            <Input
              id="place_name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Joe's Auto Repair"
              required
              data-testid="place-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="place_category">Category</Label>
            <select
              id="place_category"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white"
              data-testid="place-category-select"
            >
              <option value="restaurant">Restaurant</option>
              <option value="mechanic">Mechanic</option>
              <option value="doctor">Doctor</option>
              <option value="store">Store</option>
              <option value="service">Service</option>
              <option value="office">Office</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="place_address">Address</Label>
            <Textarea
              id="place_address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="123 Main St, City, State 12345"
              rows={2}
              data-testid="place-address-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="place_phone">Phone</Label>
              <Input
                id="place_phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                data-testid="place-phone-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="place_website">Website</Label>
              <Input
                id="place_website"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://example.com"
                data-testid="place-website-input"
              />
            </div>
          </div>
          
          {/* Experience Section */}
          <div className="pt-4 border-t border-slate-200">
            <h4 className="text-sm font-medium text-slate-700 mb-3">Experience (Optional)</h4>
            
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="would_go_back">Would you go back?</Label>
                <select
                  id="would_go_back"
                  value={formData.would_go_back || ''}
                  onChange={(e) => handleChange('would_go_back', e.target.value || null)}
                  className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white"
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="maybe">Maybe</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="experience_rating">Experience Rating</Label>
                <select
                  id="experience_rating"
                  value={formData.experience_rating || ''}
                  onChange={(e) => handleChange('experience_rating', e.target.value || null)}
                  className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white"
                >
                  <option value="">Select...</option>
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="neutral">Neutral</option>
                  <option value="bad">Bad</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="experience_notes">Experience Notes</Label>
                <Textarea
                  id="experience_notes"
                  value={formData.experience_notes}
                  onChange={(e) => handleChange('experience_notes', e.target.value)}
                  placeholder="Fast service, honest pricing..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="recommended_services">Recommended Services/Items</Label>
                <Textarea
                  id="recommended_services"
                  value={formData.recommended_services}
                  onChange={(e) => handleChange('recommended_services', e.target.value)}
                  placeholder="Oil changes, tire rotations..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="last_visit_date">Last Visit Date</Label>
                <Input
                  id="last_visit_date"
                  type="date"
                  value={formData.last_visit_date}
                  onChange={(e) => handleChange('last_visit_date', e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="place_notes">General Notes</Label>
            <Textarea
              id="place_notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any additional notes..."
              rows={2}
              data-testid="place-notes-input"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="place_is_favorite"
              checked={formData.is_favorite}
              onChange={(e) => handleChange('is_favorite', e.target.checked)}
              className="rounded"
              data-testid="place-favorite-checkbox"
            />
            <Label htmlFor="place_is_favorite" className="cursor-pointer">Add to favorites</Label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-pink-500 hover:bg-pink-600"
              data-testid="save-place-btn"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Place
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPlaceDialog;
